import {
  type FunctionComponent,
  type ReactNode,
  createContext,
  useContext,
  useMemo,
} from 'react';

import { ProgressBarItem } from '.';

export interface ProgressBarItemSingleModified {
  type: 'single';
  active: boolean;
  passed: boolean;
  first: boolean;
  last: boolean;
  title: string;
  index: number;
  key: string;
}

export interface ProgressBarItemMultiModified {
  type: 'multi';
  active: boolean;
  passed: boolean;
  first: boolean;
  last: boolean;
  title: string;
  index: number;
  substeps: ProgressBarItemSingleModified[];
  key: string;
}

export type ProgressBarItemModified =
  | ProgressBarItemSingleModified
  | ProgressBarItemMultiModified;

export interface ProgressBarContextType {
  activeIndex: number;
  modifiedItems: ProgressBarItemModified[];
  isLoading: boolean;
}

export const ProgressBarContext = createContext<ProgressBarContextType | null>(
  null,
);

export const useProgressBarContext = () =>
  useContext(ProgressBarContext) as ProgressBarContextType;

export interface ProgressBarProviderProps {
  items: ProgressBarItem[];
  lastPassed?: boolean;
  isLoading?: boolean;
  children: ReactNode;
}

const ProgressBarProvider: FunctionComponent<ProgressBarProviderProps> =
  function ({ items, lastPassed = false, isLoading = false, children }) {
    const activeIndex = useMemo(() => {
      if (items.length === 0) return -1;
      const isActive = items.map((i, index) => {
        if (i.type === 'single') {
          const { active } = i;
          return active ? index : -1;
        }
        const { substeps } = i;
        if (!substeps) return index;
        return substeps.some((si) => si.active) ? index : 0 - 1;
      });
      const maxIndex = Math.max(...isActive);
      const finalIsMulti = items[items.length - 1].type === 'multi';
      if (maxIndex === items.length - 1 && lastPassed && !finalIsMulti)
        return items.length;
      return maxIndex;
    }, [items, lastPassed]);

    const modifiedItems: ProgressBarItemModified[] = useMemo(
      () =>
        items.map((item, index) => {
          const { type, title } = item;
          const active = index === activeIndex;
          const passed = index < activeIndex;
          const first = index === 0;
          const last = index === items.length - 1;
          if (type === 'single') {
            const a: ProgressBarItemSingleModified = {
              type,
              title,
              active,
              passed,
              first,
              last,
              index,
              key: `${index}-${title}`,
            };
            return a;
          }
          const { substeps } = item;
          const subActiveIndex = Math.max(
            ...substeps.map((i, idx) => (i.active ? idx : -1)),
          );
          const b: ProgressBarItemMultiModified = {
            type,
            title,
            active,
            passed,
            first,
            last,
            index,
            key: `${index}-${title}`,
            substeps: substeps.map((subitem, subIndex) => ({
              type: 'single',
              active: subIndex === subActiveIndex,
              passed: subIndex < subActiveIndex,
              title: subitem.title,
              index: subIndex,
              first: subIndex === 0,
              last: subIndex === substeps.length - 1,
              key: `${index}-${subIndex}-${subitem.title}`,
            })),
          };
          return b;
        }),
      [items, activeIndex],
    );

    const values = useMemo(
      () => ({
        activeIndex,
        modifiedItems,
        isLoading: !isLoading,
      }),
      [activeIndex, modifiedItems, isLoading],
    );

    return (
      <ProgressBarContext.Provider value={values}>
        {children}
      </ProgressBarContext.Provider>
    );
  };

export default ProgressBarProvider;
